import { FC } from 'react';
import { Checkbox } from '@chakra-ui/react';

import { AdAttribute, AdFormDataValue } from '@/store/ad/types';
import { DescriptionListItem, DescriptionListTerm, MarkdownRenderer } from '@netiva/classifieds-ui';

import { useCurrencyFormatter } from '@/hooks';

export type AttributeDisplayProps = {
  attribute: AdAttribute;
  value: AdFormDataValue;
};

const AttributeValues: FC<AttributeDisplayProps> = ({ attribute, value }) => {
  const currencyFormatter = useCurrencyFormatter();

  switch (attribute.type) {
    case 'Boolean':
      return (
        <DescriptionListItem>
          <Checkbox isChecked={value.value?.toLowerCase() === 'true'} isReadOnly />
        </DescriptionListItem>
      );

    case 'HTML':
      return <DescriptionListItem dangerouslySetInnerHTML={{ __html: value.value || '' }} />;

    case 'Markdown':
      return <MarkdownRenderer content={value.value || ''} restrictSyntax />;

    case 'Price':
      return <DescriptionListItem>{currencyFormatter.format(Number(value.value || 0))}</DescriptionListItem>;

    case 'RangeInteger':
      return (
        <DescriptionListItem>
          {(() => {
            if (!value.value) return '';
            const parsed = JSON.parse(value.value) as { min?: string | number; max?: string | number };
            return parsed.min != parsed.max ? `${parsed.min} - ${parsed.max}` : `${parsed.min}`;
          })()}
        </DescriptionListItem>
      );
    default:
      if (value.entries?.length) {
        return (
          <>
            {value.entries.map((entry) => {
              const attributeItem = attribute.items.find((x) => x.id === entry);
              return attributeItem && <DescriptionListItem key={entry}>{attributeItem.name}</DescriptionListItem>;
            })}
          </>
        );
      }
      return <DescriptionListItem>{value.value}</DescriptionListItem>;
  }
};

export const AttributeDisplay: FC<AttributeDisplayProps> = ({ attribute, value }) => {
  return value.value || value.entries?.length ? (
    <>
      <DescriptionListTerm>{attribute.name}</DescriptionListTerm>
      <AttributeValues attribute={attribute} value={value} />
    </>
  ) : null;
};
